import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Box, Flex, Text, Center, Button, Table, TableContainer, Th, Td, Thead, Tbody, Tr, Tfoot, SimpleGrid } from '@chakra-ui/react';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import c from '../../constant';
import { gql, useLazyQuery } from '@apollo/client';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import useStore from '../../store';
import PrintLayout from '../../components/Print/PrintLayout';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import LabelDetail from '../../components/LabelDetail';
import getPositionLabel from '../../utils/getPositionLabel';
import getDepartmentLabel from '../../utils/getDepartmentLabel';
import getOfficeLabel from '../../utils/getOfficeLabel';

const QUERY_HISTORY = gql`
	query get($id: String!) {
		getUserTrainingRecords(id: $id) {
			id
			no
			courseTitle
			organiser
			certificate
			dateStart
			dateEnd
			location
			means
			locationType
			type
			attachmentFile
			bioListing
			updatedBy
			updatedAt
			updatedByUser {
				id
				username
				fullName
				fullNameKhmer
				department
				position
				avatar
			}
			user {
				id
				username
				fullName
				fullNameKhmer
				department
				position
				avatar
			}
			notification {
				id
				read
			}
		}
	}
`;

const QUERY_DETAIL = gql`
	query get($id: String!) {
		getTrainingRecordById(id: $id) {
			id
			no
			courseTitle
			organiser
			certificate
			dateStart
			dateEnd
			location
			means
			locationType
			type
			attachmentFile
			bioListing
			updatedBy
			updatedAt
			updatedByUser {
				id
				username
				fullName
				fullNameKhmer
				department
				position
				avatar
			}
			user {
				id
				username
				fullName
				fullNameKhmer
				department
				position
				office
				avatar
			}
			notification {
				id
				read
			}
		}
	}
`;

const LABEL_WIDTH = '140px';

const toStr = (value) => String(value ?? '');

function TrainingRecordHistory() {
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { currentLang: lang } = useStore((state) => state.currentLang);
	moment.locale(lang === 'kh' ? 'km' : 'en-gb');
	const { t } = useTranslation();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const id = urlParams.get('id') ? urlParams.get('id') : null;
	const [getHistory, { data }] = useLazyQuery(QUERY_HISTORY, { fetchPolicy: 'no-cache' });
	useEffect(getDetail, [id]);
	const [loadDetail, { data: dataLoadDetail }] = useLazyQuery(QUERY_DETAIL, { fetchPolicy: 'no-cache' });

	const detail = useMemo(() => {
		return data?.getUserTrainingRecords || [];
	}, [data]);

	const userDetail = dataLoadDetail?.getTrainingRecordById || null;

	function getDetail() {
		id && getHistory({ variables: { id } });
		id && loadDetail({ variables: { id } });
	}

	const localData = useMemo(() => {
		return detail?.filter((item) => item?.locationType === 'LOCAL');
	}, [detail]);

	const overseasData = useMemo(() => {
		return detail?.filter((item) => item?.locationType === 'OVERSEAS');
	}, [detail]);

	const trainingTable = (records = []) => {
		return (
			<TableContainer>
				<Table variant="striped" colorScheme="green" size="md">
					<Thead>
						<Tr>
							<Th>{t('Number')}</Th>
							<Th align="left" width="160px">
								{t('Course Title')}
							</Th>
							<Th align="left">{t('Organiser')}</Th>
							<Th align="left">{t('Date From')}</Th>
							<Th align="left">{t('Date To')}</Th>
							<Th align="left">{t('location')}</Th>
							<Th align="center">{t('Means')}</Th>
							<Th align="right">{t('Location Type')}</Th>
							<Th align="right" style={{ width: '120px' }}>
								{t('Type')}
							</Th>
						</Tr>
					</Thead>

					<Tbody>
						{records.length === 0 ? (
							<Tr>
								<Td colSpan={9} textAlign="center">
									{t('No data')}
								</Td>
							</Tr>
						) : (
							records?.map((record, index) => (
								<Tr key={record.id}>
									<Td>{record?.no ? String(record.no).padStart(4, '0') : String(index + 1).padStart(4, '0')}</Td>
									<Td>{record?.courseTitle || '-'}</Td>
									<Td>{record?.organiser || '-'}</Td>
									<Td>{record?.dateStart ? moment(record.dateStart).format('DD MMM YYYY') : '-'}</Td>
									<Td>{record?.dateEnd ? moment(record.dateEnd).format('DD MMM YYYY') : '-'}</Td>
									<Td>{record?.location || '-'}</Td>
									<Td>{t(record?.means || '-')}</Td>
									<Td>{t(record?.locationType || '-')}</Td>
									<Td>{t(record?.type || '-')}</Td>
								</Tr>
							))
						)}
					</Tbody>

					<Tfoot>
						<Tr>
							<Th>{t('Number')}</Th>
							<Th align="left" width="160px">
								{t('Course Title')}
							</Th>
							<Th align="left">{t('Organiser')}</Th>
							<Th align="left">{t('Date From')}</Th>
							<Th align="left">{t('Date To')}</Th>
							<Th align="left">{t('location')}</Th>
							<Th align="center">{t('Means')}</Th>
							<Th align="right">{t('Location Type')}</Th>
							<Th align="right">{t('Type')}</Th>
						</Tr>
					</Tfoot>
				</Table>
			</TableContainer>
		);
	};

	return (
		<>
			<PrintLayout>
				<>
					<Container maxW="container.xl" mt="16px" mb="16px">
						<BreadcrumbComponent
							list={[
								{
									name: 'Training Course',
									path: `/training-course`,
								},
								{
									name: 'Detail',
									path: `/training-course/detail?id=${id}`,
								},
								{
									name: 'History',
									path: '#',
								},
							]}
						/>
						<Center mt="5">
							<Box
								w="100%"
								bg="white"
								borderRadius={c.borderRadius}
								border="1px solid #dbdbdb"
								boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
								pb="32px"
							>
								<Flex
									alignItems="center"
									justifyContent="space-between"
									bg="#FAFAFA"
									p="16px"
									pt="8px"
									pb="8px"
									mb="16px"
									borderBottom="1px solid #dbdbdb"
								>
									<Flex justifyContent="center" alignItems="center">
										<Text fontSize="x-large" fontWeight="bold" mr="4">
											{t('History List')}
										</Text>
									</Flex>

									<Flex className="hidden-print">
										<Button
											onClick={() => window?.print()}
											leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											ml={2}
										>
											{t('Print')}
										</Button>
									</Flex>
								</Flex>

								<SimpleGrid columns={[1, null, 2]} mx={4} mb={8}>
									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Officials"
										titleDetail={lang === 'kh' ? userDetail?.user?.fullNameKhmer || '-' : userDetail?.user?.fullName || '-'}
									/>

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Position"
										titleDetail={
											userDetail?.user?.position ? t(getPositionLabel(userDetail.user.position, lang === 'kh' ? 'kh' : 'en')) : '-'
										}
									/>

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Department"
										titleDetail={userDetail?.user?.department ? toStr(t(getDepartmentLabel(userDetail.user.department))) : '-'}
									/>

									<LabelDetail
										mb={1}
										mt={2}
										labelWidth={LABEL_WIDTH}
										direction="row"
										label="Office"
										titleDetail={userDetail?.user?.office ? toStr(t(getOfficeLabel(userDetail.user.office))) : '-'}
									/>
								</SimpleGrid>

								<Box backgroundColor="#50ac37" p={1} pt={2} mx={4}>
									<Text fontWeight="bold" fontSize="20px" mb={2} mx={4}>
										{t('Domestic Training Courses and Workshops')}
									</Text>

									{trainingTable(localData)}
								</Box>

								<Box backgroundColor="#50ac37" mt={10} p={1} pt={2} mx={4}>
									<Text fontWeight="bold" fontSize="20px" mb={2} mx={4}>
										{t('Overseas Training Courses and Workshops')}
									</Text>

									{trainingTable(overseasData)}
								</Box>
							</Box>
						</Center>
					</Container>
				</>
			</PrintLayout>
		</>
	);
}

export default TrainingRecordHistory;
